import React, { memo } from 'react';
import styled from 'styled-components';
import Truncate from 'react-truncate';
import { observer } from 'mobx-react-lite';

import usePaths from 'hooks/use-paths';
import useUI from 'hooks/use-ui';
import { tracker } from 'utils/analytics';
import { Product } from 'types/graphql-overrides';

import { getCategoryForProduct } from 'shared/helpers/products';
import { EmbeddedExternalLink, Link } from 'components/core';
import { breakpoints } from 'components/carousel-slider/helpers';
import { Typography } from 'src/typography';
import { Price } from './price';
import { StrainDetails } from './strain-details';
import { Container, Content, Details, Name, ProductImage } from './product-card.components';
import { ProductCardProduct } from './product-card.types';
import { QuickAddButton } from './quick-add-button';
import { getBrandName } from './product-card.utils';

export const TEST_ID_CAROUSEL_PRODUCT_CARD = 'carousel-product-card-anchor';

type Dimensions = {
  mobile: number | null;
  desktop: number | null;
  largeDesktop?: number | null;
};
export type CarouselProductCardProps = {
  dimensions?: Dimensions;
  product: ProductCardProduct;
  productIndex: number;
  onClick?: () => void;
  onQuickAddClick?: () => void;
};
export const CarouselProductCard = memo(
  ({
    dimensions = { mobile: 150, desktop: 160, largeDesktop: 175 },
    product,
    productIndex,
    onClick,
    onQuickAddClick,
  }: CarouselProductCardProps): JSX.Element | null => {
    const brandName = getBrandName(product);

    const handleClick = (): void => {
      onClick?.();
      const analyticsProduct = product as Product;
      tracker.productClicked({ product: analyticsProduct, position: productIndex });
    };

    return (
      <StyledContainer dimensions={dimensions}>
        <Content>
          {onQuickAddClick && <QuickAddButton onClick={onQuickAddClick} productName={product.name} />}
          <LinkComponent product={product} onClick={handleClick}>
            <StyledProductImage
              dimensions={dimensions}
              product={product}
              width={dimensions.desktop}
              height={dimensions.desktop}
            />
            <StyledDetails dimensions={dimensions}>
              <Price product={product} />
              <StyledName>
                <Truncate lines={3}>
                  <Typography.Title tag='span' size='small'>
                    {product.name}
                  </Typography.Title>
                </Truncate>
              </StyledName>
              {brandName.length > 0 && (
                <Brand tag='span' size='xsmall'>
                  {brandName}
                </Brand>
              )}
              <StrainDetails product={product} />
            </StyledDetails>
          </LinkComponent>
        </Content>
      </StyledContainer>
    );
  }
);

type LinkComponentProps = {
  children: React.ReactNode;
  product: ProductCardProduct;
  onClick?: () => void;
};
const LinkComponent = observer(
  ({ children, product, onClick }: LinkComponentProps): JSX.Element => {
    const { isEmbeddedCarousel } = useUI();
    const { href, route } = usePaths({ category: getCategoryForProduct(product), product });

    if (isEmbeddedCarousel) {
      return (
        <EmbeddedExternalLink href={href}>
          <a href={href} onClick={onClick}>
            {children}
          </a>
        </EmbeddedExternalLink>
      );
    }

    return (
      <Link href={href} route={route}>
        <a href={href} onClick={onClick} data-testid={TEST_ID_CAROUSEL_PRODUCT_CARD}>
          {children}
        </a>
      </Link>
    );
  }
);

const StyledContainer = styled(Container)`
  padding: 0;
  min-width: ${({ dimensions }) => `${String(dimensions.desktop)}px`};
  @media (min-width: ${breakpoints.mobile}px) {
    padding: 15px;
    max-width: 214px;
  }
  @media (max-width: ${breakpoints.mobile}px) {
    max-width: ${({ dimensions }) => `${String(dimensions.mobile)}px`};
  }
`;

const StyledProductImage = styled(ProductImage)`
  @media (max-width: ${breakpoints.mobile}px) {
    width: ${({ dimensions }) => `${String(dimensions.mobile)}px`};
    height: ${({ dimensions }) => `${String(dimensions.mobile)}px`};
  }
`;

const StyledDetails = styled(Details)`
  max-width: ${({ dimensions }) => `${String(dimensions.mobile)}px`};
  @media (min-width: ${breakpoints.largeTablet}px) {
    max-width: ${({ dimensions }) => `${String(dimensions.desktop)}px`};
  }
`;

const StyledName = styled(Name)`
  @media (min-width: ${breakpoints.largeTablet}px) {
    font-size: ${({ cardSize }) => (cardSize === `large` ? `15px` : `14px`)};
  }
`;

const Brand = styled(Typography.Body)`
  color: ${({ theme }) => theme.colors.primaryBlack};
`;
